// src/auth/useAuthInternal.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  api,
  clearAuthSession,
  getAuthToken,
  getRefreshToken,
  getStoredAuthAddress,
  setAuthSession,
  setStoredAuthAddress,
} from "../lib/api";
import {
  connectWalletConnect,
  disconnectWalletConnect,
  resolveWalletConnectSession,
  getCurrentProvider,
  getCurrentSigner,
  getCurrentAddress,
  isAppKitConnected,
  getAppKitAddress,
  isSameWallet,
  setWalletRequestLabel,
  clearWalletRequestLabel,
  humanizeWalletRequestError,
} from "../wallet/walletClient";


/**
 * ---------------------------------------------------------------------
 * ChainForge — useAuth
 * ---------------------------------------------------------------------
 *
 * Responsibilities
 * - Wallet-based sign in
 * - Access + refresh token session handling
 * - Session restore via /auth/me
 * - Cross-tab auth sync
 * - Same-tab auth sync
 * - Safe Wallet request locking
 * - Clean logout + failed refresh recovery
 * - Single-flight login protection
 * - Cooldown persistence for auth rate limiting
 *
 * Expected api.js exports
 * - api
 * - getAuthToken()
 * - getRefreshToken()
 * - setAuthSession({ accessToken, refreshToken })
 * - clearAuthSession()
 * - getStoredAuthAddress()
 * - setStoredAuthAddress(address)
 */

const AUTH_CHANGED_EVENT = "chainforge:auth-changed";
const AUTH_COOLDOWN_KEY = "chainforge_auth_cooldown_until";

function emitAuthChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AUTH_CHANGED_EVENT));
  }
}

function safeApiError(err, fallback = "Authentication failed.") {
  return (
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
}

function isEvmAddress(v) {
  return typeof v === "string" && /^0x[a-fA-F0-9]{40}$/.test(String(v).trim());
}

function readStoredCooldownUntil() {
  try {
    if (typeof window === "undefined") return 0;
    const raw = window.sessionStorage.getItem(AUTH_COOLDOWN_KEY);
    const value = Number(raw || 0);
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}

function writeStoredCooldownUntil(value) {
  try {
    if (typeof window === "undefined") return;

    if (!value || value <= Date.now()) {
      window.sessionStorage.removeItem(AUTH_COOLDOWN_KEY);
      return;
    }

    window.sessionStorage.setItem(AUTH_COOLDOWN_KEY, String(value));
  } catch {
    // ignore storage errors
  }
}

/**
 * Wallet request lock:
 * prevents duplicate concurrent wallet requests,
 * especially the -32002 pending request issue.
 */
function useWalletRequestLock() {
  const lockRef = useRef(false);

  const withWalletRequestLock = useCallback(async (fn) => {
    if (lockRef.current) {
      const err = new Error(
        "A wallet request is already pending. Open your wallet app or browser extension and complete it.",
      );
      err.code = -32002;
      throw err;
    }

    lockRef.current = true;
    try {
      return await fn();
    } finally {
      lockRef.current = false;
    }
  }, []);

  return withWalletRequestLock;
}

export default function useAuthInternal() {
  const [accessToken, setAccessTokenState] = useState(
    () => getAuthToken() || "",
  );
  const [refreshToken, setRefreshTokenState] = useState(
    () => getRefreshToken() || "",
  );
  const [address, setAddress] = useState(() => getStoredAuthAddress() || "");
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [restoreLoading, setRestoreLoading] = useState(true);

  const [userId, setUserId] = useState("");
  const [walletId, setWalletId] = useState("");
  const [role, setRole] = useState("user");
  const [profile, setProfile] = useState(null);

  const withWalletRequestLock = useWalletRequestLock();
  const restoreInFlightRef = useRef(null);
  const loginInFlightRef = useRef(null);
  const authCooldownUntilRef = useRef(0);
  const connectInFlightRef = useRef(null);

  const [authCooldownUntil, setAuthCooldownUntil] = useState(0);
  const [nowTs, setNowTs] = useState(Date.now());

  const isAuthed = Boolean(accessToken);

  useEffect(() => {
    const storedUntil = readStoredCooldownUntil();
    if (storedUntil > Date.now()) {
      authCooldownUntilRef.current = storedUntil;
      setAuthCooldownUntil(storedUntil);
    }
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNowTs(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, []);

  const isAuthCoolingDown = useMemo(() => {
    return nowTs < authCooldownUntil;
  }, [nowTs, authCooldownUntil]);

  const authCooldownRemainingMs = useMemo(() => {
    return Math.max(0, authCooldownUntil - nowTs);
  }, [authCooldownUntil, nowTs]);

  const authCooldownRemainingSeconds = useMemo(() => {
    return Math.ceil(authCooldownRemainingMs / 1000);
  }, [authCooldownRemainingMs]);

  const session = useMemo(() => {
    if (!accessToken) return null;

    return {
      token: accessToken,
      accessToken,
      refreshToken: refreshToken || null,
      address: address || null,
      userId: userId || null,
      walletId: walletId || null,
      role: role || "user",
      profile: profile || null,
    };
  }, [accessToken, refreshToken, address, userId, walletId, role, profile]);

  const syncFromStorage = useCallback(() => {
    setAccessTokenState(getAuthToken() || "");
    setRefreshTokenState(getRefreshToken() || "");
    setAddress(getStoredAuthAddress() || "");
  }, []);

  const clearAppSessionOnly = useCallback(() => {
    clearAuthSession();

    try {
      window.localStorage.removeItem("chainforge_wallet_address");
    } catch {
      // ignore storage errors
    }

    setAccessTokenState("");
    setRefreshTokenState("");
    setAddress("");
    setUserId("");
    setWalletId("");
    setRole("user");
    setProfile(null);
  }, []);

  /**
   * Restore session once on mount / token changes.
   * If access token exists, validate it with /auth/me.
   * If invalid, api.js may already try silent refresh.
   * If still invalid, clear local session.
   */
  useEffect(() => {
    let mounted = true;

    async function restoreSession() {
      if (!accessToken) {
        if (mounted) setRestoreLoading(false);
        return;
      }

      try {
        if (!restoreInFlightRef.current) {
          restoreInFlightRef.current = api.get("/auth/me");
        }

        const res = await restoreInFlightRef.current;
        const data = res?.data || {};
        const resolvedAddress = String(data?.address || "").trim();
        const sessionUserId = String(
          data?.session?.userId || data?.profile?.id || "",
        ).trim();
        const sessionWalletId = String(
          data?.session?.walletId || "",
        ).trim();
        const sessionRole = String(
          data?.session?.role || data?.profile?.role || "user",
        )
          .trim()
          .toLowerCase();

        if (!mounted) return;

        if (isEvmAddress(resolvedAddress)) {
          setStoredAuthAddress(resolvedAddress);

          try {
            window.localStorage.setItem(
              "chainforge_wallet_address",
              String(resolvedAddress || "").trim(),
            );
          } catch {
            // ignore storage errors
          }

          setAddress(resolvedAddress);
          setUserId(sessionUserId);
          setWalletId(sessionWalletId);
          setRole(sessionRole || "user");
          setProfile(data?.profile || null);
        } else {
          syncFromStorage();
        }
      } catch {
        if (!mounted) return;
        clearAppSessionOnly();
      } finally {
        restoreInFlightRef.current = null;
        if (mounted) setRestoreLoading(false);
      }
    }

    restoreSession();

    return () => {
      mounted = false;
    };
  }, [accessToken, syncFromStorage, clearAppSessionOnly]);

  /**
   * Cross-tab + same-tab synchronization
   */
  useEffect(() => {
    const onStorage = () => {
      syncFromStorage();
    };

    const onAuthChanged = () => {
      syncFromStorage();
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener(AUTH_CHANGED_EVENT, onAuthChanged);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
    };
  }, [syncFromStorage]);

  /**
   * login()
   * 1) Resolve wallet address
   * 2) Request nonce/message from backend
   * 3) Ask wallet to sign
   * 4) Verify signature
   * 5) Persist access + refresh session
   *
   * Production hardening:
   * - single-flight protection
   * - cooldown after rate limit
   * - no repeated auth fan-out
   */
  const login = useCallback(async () => {
    if (loginInFlightRef.current) {
      return loginInFlightRef.current;
    }

    if (Date.now() < authCooldownUntilRef.current) {
      const remainingSeconds = Math.max(
        1,
        Math.ceil((authCooldownUntilRef.current - Date.now()) / 1000),
      );

      const result = {
        ok: false,
        error: `Too many authentication attempts. Please wait ${remainingSeconds} seconds and try again.`,
      };

      setAuthError(result.error);
      return result;
    }

    const loginPromise = (async () => {
      setAuthLoading(true);
      setAuthError("");

      try {
        let signer = null;
        let walletAddress = "";

        try {
          const appKitAddress = String(getAppKitAddress() || "").trim();
          const appKitActive = isAppKitConnected() && isEvmAddress(appKitAddress);

          // 1) AppKit/Reown is authoritative whenever it is connected.
          //    Resolve a fully stable wallet session from walletClient.
          if (appKitActive) {
            const currentAddress = String(getCurrentAddress() || "").trim();
            const currentSigner = getCurrentSigner();

            // ✅ If wallet already stabilized, DO NOT resolve again
            if (currentSigner && isSameWallet(currentAddress, appKitAddress)) {
              signer = currentSigner;
              walletAddress = currentAddress;
            } else {
              // 🔁 Only resolve when needed (first connect / after switch)
              const wallet = await resolveWalletConnectSession();
              signer = wallet.signer;
              walletAddress = String(wallet.address || "").trim();
            }
          }
          else {
            // 2) Otherwise reuse in-memory wallet first
            const currentSigner = getCurrentSigner();
            const currentProvider = getCurrentProvider();
            const currentAddress = String(getCurrentAddress() || "").trim();

            if (currentSigner && currentAddress) {
              signer = currentSigner;
              walletAddress = currentAddress;
            } else if (currentProvider) {
              try {
                const nextSigner = await currentProvider.getSigner();
                const nextAddress = String(await nextSigner.getAddress()).trim();

                if (isEvmAddress(nextAddress)) {
                  signer = nextSigner;
                  walletAddress = nextAddress;
                }
              } catch {
                // ignore and continue
              }
            }
          }

          if (!signer || !walletAddress) {
            throw new Error("Connect a wallet first.");
          }
        } catch (err) {
          throw new Error(err?.message || "Wallet connection failed.");
        }

        if (!isEvmAddress(walletAddress)) {
          throw new Error("Invalid wallet address.");
        }
        const signerAddress = String(await signer.getAddress()).trim();
        const appKitSelectedAddress = String(getAppKitAddress() || "").trim();

        // 🔥 Only warn, do NOT block login
        if (
          appKitSelectedAddress &&
          !isSameWallet(appKitSelectedAddress, signerAddress)
        ) {
          console.warn("AppKit lag detected", {
            appKitSelectedAddress,
            signerAddress,
          });
        }

        // signer is source of truth for auth
        walletAddress = signerAddress;

        // Step 1: auth start
        const startRes = await api.post("/auth/start", {
          address: walletAddress,
        });

        const message = String(startRes?.data?.message || "").trim();
        if (!message) {
          throw new Error("Auth start failed (missing message).");
        }

        // Step 2: sign message
        let signature = "";
        try {
          signature = await withWalletRequestLock(() => signer.signMessage(message));
        } catch (err) {
          const rawMessage = String(err?.message || "").toLowerCase();
          const rawCode = err?.code;

          const isUserRejected =
            rawCode === 4001 ||
            rawCode === "ACTION_REJECTED" ||
            rawMessage.includes("user rejected") ||
            rawMessage.includes("rejected the request") ||
            rawMessage.includes("ethers-user-denied");

          if (isUserRejected) {
            throw new Error("You cancelled the wallet signature request.");
          }

          if (rawCode === -32002) {
            throw new Error(
              "Your wallet has a pending request. Open your wallet app or browser extension to continue.",
            );
          }

          throw new Error("Signature request failed. Please try again.");
        }

        if (!signature) {
          throw new Error("Signature missing.");
        }

        // Step 3: verify signature
        const verifyRes = await api.post("/auth/verify", {
          address: walletAddress,
          signature,
        });

        const verifyData = verifyRes?.data || {};

        const nextAccessToken = String(
          verifyData?.accessToken || verifyData?.token || "",
        ).trim();

        const nextRefreshToken = String(
          verifyData?.refreshToken || "",
        ).trim();

        const returnedAddress = String(
          verifyData?.address || walletAddress,
        ).trim();

        const nextUserId = String(verifyData?.userId || "").trim();
        const nextWalletId = String(verifyData?.walletId || "").trim();
        const nextRole = String(verifyData?.role || "user")
          .trim()
          .toLowerCase();

        if (!nextAccessToken) {
          throw new Error("Auth verify failed (missing access token).");
        }

        if (!nextRefreshToken) {
          throw new Error("Auth verify failed (missing refresh token).");
        }

        const normalizedAddress = isEvmAddress(returnedAddress)
          ? returnedAddress
          : walletAddress;

        setAuthSession({
          accessToken: nextAccessToken,
          refreshToken: nextRefreshToken,
          address: normalizedAddress,
        });

        try {
          window.localStorage.setItem(
            "chainforge_wallet_address",
            String(normalizedAddress || "").trim(),
          );
        } catch {
          // ignore storage errors
        }

        setAccessTokenState(nextAccessToken);
        setRefreshTokenState(nextRefreshToken);
        setAddress(normalizedAddress);
        setUserId(nextUserId);
        setWalletId(nextWalletId);
        setRole(nextRole || "user");
        setProfile((prev) =>
          prev && prev.id === nextUserId
            ? prev
            : prev
        );

        setAuthCooldownUntil(0);
        authCooldownUntilRef.current = 0;
        writeStoredCooldownUntil(0);

        emitAuthChanged();

        return {
          ok: true,
          address: normalizedAddress,
          token: nextAccessToken,
          accessToken: nextAccessToken,
          refreshToken: nextRefreshToken,
          userId: nextUserId || null,
          walletId: nextWalletId || null,
          role: nextRole || "user",
        };
      } catch (err) {
        const message = safeApiError(err, "Login failed.");

        const isRateLimited =
          err?.response?.status === 429 ||
          /too many authentication attempts/i.test(message) ||
          /too many requests/i.test(message);

        if (isRateLimited) {
          const until = Date.now() + 60 * 1000;
          authCooldownUntilRef.current = until;
          setAuthCooldownUntil(until);
          writeStoredCooldownUntil(until);

          const rateLimitMessage =
            "Too many authentication attempts. Please wait 60 seconds and try again.";

          setAuthError(rateLimitMessage);

          return {
            ok: false,
            error: rateLimitMessage,
          };
        }

        setAuthError(message);

        return {
          ok: false,
          error: message,
        };
      } finally {
        setAuthLoading(false);
        loginInFlightRef.current = null;
      }
    })();

    loginInFlightRef.current = loginPromise;
    return loginPromise;
  }, [withWalletRequestLock]);

  /**
   * logout()
   * Clears local session.
   * Backend remains stateless unless you later add refresh revocation.
   */
  const logout = useCallback(async () => {
    try {
      clearAppSessionOnly();
      await disconnectWalletConnect();
    } catch {
      // ignore disconnect cleanup errors
    } finally {
      setAuthError("");
      emitAuthChanged();
    }
  }, [clearAppSessionOnly]);

  const connectWithWalletConnectAndLogin = useCallback(async () => {
    if (connectInFlightRef.current) {
      return connectInFlightRef.current;
    }

    const connectPromise = (async () => {
      setAuthError("");
      setWalletRequestLabel("Open your wallet app or browser extension and finish the request");

      try {
        const existingAppKitAddress = String(getAppKitAddress() || "").trim();

        // Already connected in AppKit -> just sign in
        if (isAppKitConnected() && isEvmAddress(existingAppKitAddress)) {
          return await login();
        }

        const walletResult = await connectWalletConnect();
        const loginResult = await login();

        if (!loginResult?.ok) {
          return loginResult;
        }

        const selectedAddress = String(walletResult?.address || "").trim();
        const authedAddress = String(loginResult?.address || "").trim();

        if (
          selectedAddress &&
          authedAddress &&
          !isSameWallet(selectedAddress, authedAddress)
        ) {
          const mismatchMessage =
            "Wallet selection changed before login completed. Please try again.";
          setAuthError(mismatchMessage);
          return { ok: false, error: mismatchMessage };
        }

        return loginResult;
      } catch (err) {
        const recoveredAddress = String(getAppKitAddress() || "").trim();

        if (isAppKitConnected() && isEvmAddress(recoveredAddress)) {
          return await login();
        }

        const message = humanizeWalletRequestError(
          err,
          "WalletConnect login failed.",
        );

        setAuthError(message);
        return { ok: false, error: message };
      } finally {
        clearWalletRequestLabel();
        connectInFlightRef.current = null;
      }
    })();

    connectInFlightRef.current = connectPromise;
    return connectPromise;
  }, [login]);

  const switchWallet = useCallback(async () => {
    setAuthError("");

    if (authLoading) {
      return {
        ok: false,
        error: "Authentication already in progress.",
      };
    }

    const currentAuthAddress = String(getStoredAuthAddress() || "").trim();

    try {
      // 1) Clear only app auth first
      clearAppSessionOnly();

      // 2) Fully disconnect wallet session
      await disconnectWalletConnect();

      // 3) Reconnect and sign in again with the newly selected wallet
      const result = await connectWithWalletConnectAndLogin();

      if (!result?.ok) {
        return result;
      }

      const nextAddress = String(result?.address || "").trim();

      if (!nextAddress) {
        return {
          ok: false,
          error: "No wallet address returned after reconnect.",
        };
      }

      if (isSameWallet(nextAddress, currentAuthAddress)) {
        return {
          ok: true,
          address: nextAddress,
          switched: false,
          reconnected: true,
        };
      }

      emitAuthChanged();

      return {
        ok: true,
        address: nextAddress,
        switched: true,
        reconnected: true,
      };
    } catch (err) {
      const message = safeApiError(
        err,
        "Wallet switch failed. Please reconnect and try again.",
      );
      setAuthError(message);

      return {
        ok: false,
        error: message,
      };
    }
  }, [authLoading, clearAppSessionOnly, connectWithWalletConnectAndLogin]);




  /**
   * Manual refresh helper
   * Usually not needed because api.js handles 401 -> refresh silently.
   */
  const refreshSession = useCallback(async () => {
    const currentRefreshToken = getRefreshToken() || "";

    if (!currentRefreshToken) {
      clearAppSessionOnly();
      return { ok: false, error: "No refresh token available." };
    }

    try {
      const res = await api.post("/auth/refresh", {
        refreshToken: currentRefreshToken,
      });

      const data = res?.data || {};

      const nextAccessToken = String(data?.accessToken || "").trim();
      const nextRefreshToken = String(data?.refreshToken || "").trim();
      const nextAddress = String(data?.address || address || "").trim();
      const nextUserId = String(
        data?.session?.userId || data?.userId || "",
      ).trim();
      const nextWalletId = String(
        data?.session?.walletId || data?.walletId || "",
      ).trim();
      const nextRole = String(
        data?.session?.role || data?.role || "user",
      )
        .trim()
        .toLowerCase();

      if (!nextAccessToken || !nextRefreshToken) {
        throw new Error("Refresh failed (missing tokens).");
      }

      setAuthSession({
        accessToken: nextAccessToken,
        refreshToken: nextRefreshToken,
      });

      setAccessTokenState(nextAccessToken);
      setRefreshTokenState(nextRefreshToken);
      setUserId(nextUserId);
      setWalletId(nextWalletId);
      setRole(nextRole || "user");
      setProfile(data?.profile || null);

      if (isEvmAddress(nextAddress)) {
        setStoredAuthAddress(nextAddress);

        try {
          window.localStorage.setItem(
            "chainforge_wallet_address",
            String(nextAddress || "").trim(),
          );
        } catch {
          // ignore storage errors
        }

        setAddress(nextAddress);
      }

      emitAuthChanged();

      return {
        ok: true,
        accessToken: nextAccessToken,
        refreshToken: nextRefreshToken,
        address: isEvmAddress(nextAddress) ? nextAddress : null,
        userId: nextUserId || null,
        walletId: nextWalletId || null,
        role: nextRole || "user",
      };

    } catch (err) {
      clearAppSessionOnly();
      return {
        ok: false,
        error: safeApiError(err, "Session refresh failed."),
      };
    }
  }, [address, clearAppSessionOnly]);

  const requireAuthed = useCallback(() => {
    if (!isAuthed) {
      throw new Error("Sign in required.");
    }
  }, [isAuthed]);

  return {
    session,
    isAuthed,
    authLoading,
    restoreLoading,
    authReady: !restoreLoading,
    authError,
    authCooldownUntil,
    isAuthCoolingDown,
    authCooldownRemainingMs,
    authCooldownRemainingSeconds,
    login,
    logout,
    switchWallet,
    connectWithWalletConnectAndLogin,
    refreshSession,
    requireAuthed,

    // Convenience fields
    address: session?.address || "",
    userId: session?.userId || "",
    walletId: session?.walletId || "",
    role: session?.role || "user",
    profile: session?.profile || null,
    email: session?.profile?.email || "",
    emailVerified: Boolean(session?.profile?.emailVerified),
    token: session?.token || "",
    accessToken: session?.accessToken || "",
    refreshToken: session?.refreshToken || "",
  };
}